import React, { useState, useEffect, useCallback } from 'react';
import { useContext } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { AlertCircle, Ticket } from 'lucide-react';
import { toast } from 'sonner';

const UsageCounter = ({ refreshKey }) => {
  const { token, user } = useContext(AuthContext);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUsage = useCallback(async () => {
    if (!token || token === 'guest-token') return;
    try {
      const response = await axios.get(`${API}/auth/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch on mount and when user's subscription_tier changes or refreshKey changes
  useEffect(() => {
    fetchUsage();
  }, [fetchUsage, user?.subscription_tier, refreshKey]);

  // Also refresh on window focus
  useEffect(() => {
    const handleFocus = () => fetchUsage();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchUsage]);

  if (loading || !usage) return null;

  const totalPossible = usage.limit + (usage.starter_balance || 0);
  const isAtLimit = usage.remaining === 0;
  const isNearLimit = !isAtLimit && (usage.remaining / (totalPossible || 1)) < 0.2;

  const monthlyRemaining = Math.max(0, usage.limit - usage.monthly_generations);
  const starterRemaining = usage.starter_balance || 0;
  const folioRemaining = usage.folio_balance || 0;

  // Calculate segments for the progress bar
  const monthlyUsedWidth = (Math.min(usage.monthly_generations, usage.limit) / (totalPossible || 1)) * 100;
  const monthlyRemainingWidth = (monthlyRemaining / (totalPossible || 1)) * 100;
  const starterWidth = (starterRemaining / (totalPossible || 1)) * 100;

  return (
    <div 
      className="sketch-border p-2 rounded-xl transition-colors duration-300"
      style={{ 
        background: isAtLimit ? '#fee2e2' : isNearLimit ? '#fef3c7' : 'var(--bg-secondary)',
        color: 'var(--text-primary)'
      }}
    >
      <div className="flex items-center justify-between mb-1 px-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-sketch font-bold uppercase tracking-wider opacity-70">
            Credits Remaining
          </span>
          {isAtLimit && <AlertCircle className="w-3 h-3 text-red-600" />}
          {isNearLimit && <AlertCircle className="w-3 h-3 text-yellow-600" />}
        </div>
        <span className="text-xs font-bold">
          {usage.remaining} images or stories available
        </span>
      </div>
      
      <div className="h-4 w-full rounded-full overflow-hidden flex bg-gray-200 dark:bg-gray-800 border-2 border-black dark:border-white mb-1.5">
        {/* Segment 1: Monthly Used (Muted/Spent) */}
        <div 
          className="h-full transition-all duration-500" 
          style={{ width: `${monthlyUsedWidth}%`, background: '#475569' }} 
          title={`Used: ${Math.min(usage.monthly_generations, usage.limit)}`}
        />
        
        {/* Segment 2: Monthly Remaining (Vibrant Purple) */}
        <div 
          className="h-full transition-all duration-500" 
          style={{ width: `${monthlyRemainingWidth}%`, background: '#a855f7' }} 
          title={`Monthly Left: ${monthlyRemaining}`}
        />
        
        {/* Segment 3: Starter Balance (Bright Blue) */}
        <div
          className="h-full transition-all duration-500" 
          style={{ width: `${starterWidth}%`, background: '#3b82f6' }}
          title={`One-off credit: ${starterRemaining}`}
        />
      </div>

      {/* Compact Legend */}
      <div className="flex items-center justify-between px-1 text-[10px] font-bold uppercase opacity-70">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full" style={{ background: '#a855f7' }} />
          <span>Subscription Plan Credits: {monthlyRemaining}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full" style={{ background: '#3b82f6' }} />
          <span>One-off Credits: {starterRemaining}</span>
        </div>
        {folioRemaining > 0 && (
          <div className="flex items-center gap-1">
            <Ticket className="w-2 h-2" />
            <span>Folio Passes: {folioRemaining}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageCounter;
